/**
 * W2-TRAIN-WEEK (TWK-4, 2.0 build spec D2 and D3) — the two optional lines of a Train day card.
 *
 * PACE, ONCE. A day prints at most one pace, "5:40 per km" ("per mile" for a mile plan). The engine stores a
 * single pace per day, so the range in the frames ("5:40 to 6:00 per km") waits for SRV-PACERANGE (D2): this
 * never prints a range, and it never prints a pace the plan does not state.
 *
 * WHERE IT COMES FROM (FIX-TRAIN, night critic finding 3). The engine's own pace, `expanded_details.pace_target`,
 * which the adapter (`hooks/useWeeklyPlan.ts`) carries on the `Day` as `paceTarget`. Then the fixture `pace`
 * field ("9:30 /mi"), then the day's plan text. The first of those that states a pace decides, and a source that
 * states MORE than one (two different paces, or a range) prints nothing, rather than a guess at which one the
 * athlete should read: the old reading took the first "M:SS/unit" token, so "2 km at 5:40/km then 3 km at 4:50/km"
 * read "5:40 per km", and an engine day that kept its pace only in `pace_target` read none.
 *
 * ONE UNIT (B313). The pace is printed in the unit it was written in, and only when that is the unit the athlete
 * reads in (`paceUnit`, carried by the adapter): a "9:30/mi" over "Easy run, 10 km" printed nothing wrong here
 * would still be a wrong value, so it prints nothing. How paces are resolved is not this surface's decision, and
 * neither is converting one. A `Day` that does not say its unit (not from the adapter) prints no pace.
 *
 * "YOU RAN 6.2 KM". Printed only where the existing actuals gate allows (D3): the adapter attaches `actual`
 * only when the client flag `b19_actuals_v1` is on AND a stored actual exists, so a day with `actual` and a
 * logged `actualDistance` is exactly a day the gate lets through. Nothing else prints it.
 */
import type { Day } from '../../fixtures/plan';
import type { DistanceUnit } from '../../lib/actualForPlanDay';

/** What `dayPace` reads of a `Day`: the three existing fields and the two the adapter adds for it. */
export type PaceDay = Pick<Day, 'pace' | 'details' | 'rest'> & {
  /** `expanded_details.pace_target` as the engine stored it (`hooks/useWeeklyPlan.ts`, `DayWithType`). */
  paceTarget?: string | null;
  /** The unit the athlete reads in (`DayWithType.paceUnit`). */
  paceUnit?: DistanceUnit | null;
};

/**
 * One pace expression: "8:30/mi", "5:40 / km", "9:30 per mile", or a range "5:40-6:00/km", "5:40 to 6:00 per km".
 * Durations ("2:30 warm-up") carry no unit and never match. Group 1 is the pace (the first of a range), group 2 the
 * end of a range, group 3 the unit.
 */
const PACE_EXPR = new RegExp(
  `(\\d{1,2}:\\d{2})(?:\\s*(?:[-${String.fromCharCode(0x2013, 0x2014)}]|to)\\s*(\\d{1,2}:\\d{2}))?` +
    '\\s*(?:\\/|per\\s+)\\s*(mi(?:les?)?|km|kilomet(?:er|re)s?)\\b',
  'gi',
);

type Pace = { value: string; unit: DistanceUnit };

/**
 * `undefined`: the text states no pace. `null`: it states more than one (two different paces, a range, or the same
 * pace in two units). Otherwise the one pace. The same pace said twice is one pace.
 */
function onlyPace(text: unknown): Pace | null | undefined {
  if (typeof text !== 'string') return undefined;
  const seen = new Map<string, Pace>();
  let range = false;
  for (const m of text.matchAll(PACE_EXPR)) {
    if (m[2]) range = true;
    const unit: DistanceUnit = m[3].toLowerCase().startsWith('mi') ? 'mi' : 'km';
    seen.set(`${m[1]}/${unit}`, { value: m[1], unit });
  }
  if (seen.size === 0) return undefined;
  if (range || seen.size > 1) return null;
  return [...seen.values()][0];
}

/** "5:40 per km" or "8:30 per mile", or null when the day states no single pace in the athlete's unit. */
export function dayPace(day: PaceDay): string | null {
  if (day.rest) return null;
  const unit = day.paceUnit;
  if (unit !== 'mi' && unit !== 'km') return null;
  for (const source of [day.paceTarget, day.pace, day.details]) {
    const pace = onlyPace(source);
    if (pace === undefined) continue;
    // The first source that states a pace decides: ambiguous, or in the other unit, prints nothing.
    if (pace === null || pace.unit !== unit) return null;
    return `${pace.value} per ${pace.unit === 'mi' ? 'mile' : 'km'}`;
  }
  return null;
}

/** "You ran 6.2 km", or null unless the actuals gate let the day carry a logged distance. */
export function ranLine(day: Pick<Day, 'actual' | 'actualDistance'>): string | null {
  if (!day.actual) return null;
  const logged = day.actualDistance;
  if (!logged || !(logged.value > 0)) return null;
  return `You ran ${logged.value} ${logged.unit}`;
}
